import { AnalyticsEvents, trackEvent } from "@axqora/analytics";
import { useAppStoreHref } from "@axqora/ui";
import { Camera } from "lucide-react";
import { routePath } from "../assets/routes";
import { screenshots } from "../assets/screenshots";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { prismarkConfig } from "../config";
import { localizedPaths, saveLocale } from "../i18n/routes";
import type { LandingContent, Locale } from "../content/types";

type HeaderProps = {
  content: LandingContent;
  locale: Locale;
  page?: "landing" | "privacy" | "support" | "terms";
};

export function Header({ content, locale, page = "landing" }: HeaderProps) {
  const appStoreHref = useAppStoreHref(prismarkConfig.appStoreUrl[locale], locale);
  const homeHref = routePath(localizedPaths.landing[locale]);
  const privacyHref = routePath(localizedPaths.privacy[locale]);
  const supportHref = routePath(localizedPaths.support[locale]);
  const showLandingNav = page === "landing";

  const trackPage = page === "landing" ? "home" : page;
  const trackNavigation = (event: typeof AnalyticsEvents.PrivacyClick | typeof AnalyticsEvents.SupportClick | typeof AnalyticsEvents.CtaClick, target: string) => {
    trackEvent(event, {
      product: "prismark",
      page: trackPage,
      locale,
      target
    });
  };

  return (
    <header className="site-header">
      <nav className="site-nav" aria-label="Primary navigation">
        <a className="brand-link" href={homeHref}>
          <img src={screenshots.icon} alt="" width="32" height="32" />
          <span>Prismark</span>
        </a>

        {showLandingNav ? (
          <div className="nav-links">
            <a href="#features">{content.nav.features}</a>
            <a href="#pro">{content.nav.pro}</a>
            <a href="#download" onClick={() => trackNavigation(AnalyticsEvents.CtaClick, "nav_download")}>
              {content.nav.download}
            </a>
            <a href={privacyHref} onClick={() => trackNavigation(AnalyticsEvents.PrivacyClick, "header_privacy")}>
              {content.nav.privacy}
            </a>
            <a href={supportHref} onClick={() => trackNavigation(AnalyticsEvents.SupportClick, "header_support")}>
              {content.nav.support}
            </a>
          </div>
        ) : null}

        <div className="nav-actions">
          {showLandingNav ? (
            <a
              className="nav-download"
              href={appStoreHref}
              onClick={() => trackNavigation(AnalyticsEvents.CtaClick, "header_download")}
              rel="noreferrer"
              target="_blank"
            >
              <Camera size={16} aria-hidden="true" />
              {content.hero.primaryCta}
            </a>
          ) : null}
          <LanguageSwitcher
            locale={locale}
            context={page}
            onSwitch={(to) => {
              saveLocale(to);
              trackEvent(AnalyticsEvents.LanguageSwitch, {
                product: "prismark",
                page: trackPage,
                from: locale,
                to
              });
            }}
          />
        </div>
      </nav>
    </header>
  );
}
